import React, { useEffect, useState, useCallback } from "react";
import {
  Lock,
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  Clock,
  AlertCircle,
  Loader2,
  Inbox,
  FileText,
  TrendingUp,
  Calendar,
  ChevronDown,
} from "lucide-react";
import {
  verifyAdmin,
  listBookings,
  listQuotes,
  fetchStats,
  updateBookingStatus,
  updateQuoteStatus,
  listAssessments,
  updateAssessmentStatus,
} from "../lib/api";
import AdminClientsPanel from "../components/AdminClientsPanel";
import { FileDown } from "lucide-react";
import { downloadMobileApiSpec } from "../lib/api";
import AdminSessionsPanel from "../components/AdminSessionsPanel";
import AdminDoctorsPanel from "../components/AdminDoctorsPanel";

const PIN_KEY = "lc_admin_pin";

const BOOKING_STATUSES = ["new", "contacted", "closed"];
const QUOTE_STATUSES = ["new", "quoted", "won", "lost"];
const ASSESSMENT_STATUSES = ["new", "reviewing", "scheduled", "completed"];

const STATUS_STYLES = {
  new: "bg-[var(--lc-moss)]/15 text-[var(--lc-moss-dark)] border-[var(--lc-moss)]/30",
  contacted: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  closed: "bg-neutral-200 text-neutral-700 border-neutral-300",
  quoted: "bg-amber-500/10 text-amber-800 border-amber-500/30",
  won: "bg-green-600/15 text-green-800 border-green-600/30",
  lost: "bg-red-500/10 text-red-700 border-red-400/30",
  reviewing: "bg-amber-500/10 text-amber-800 border-amber-500/30",
  scheduled: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  completed: "bg-green-600/15 text-green-800 border-green-600/30",
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ value, options, onChange, loading }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold tracking-wider uppercase border rounded-full transition-colors ${STATUS_STYLES[value] || ""}`}
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        {value}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white border border-[var(--lc-line)] rounded-md shadow-lg overflow-hidden min-w-[140px]">
            {options.map((opt) => (
              <button
                key={opt}
                onClick={() => {
                  setOpen(false);
                  if (opt !== value) onChange(opt);
                }}
                className={`block w-full text-left px-3 py-2 text-xs font-semibold tracking-wider uppercase hover:bg-[var(--lc-cream-soft)] ${opt === value ? "text-[var(--lc-moss-dark)]" : ""}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyAdmin(pin);
      localStorage.setItem(PIN_KEY, pin);
      onLogin(pin);
    } catch (err) {
      setError(err?.response?.status === 429 ? err.response.data.detail : "Invalid PIN. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-[var(--lc-cream)] px-5 pt-[100px]">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[var(--lc-line)] rounded-md p-8 lg:p-10"
      >
        <div className="w-12 h-12 rounded-full bg-[var(--lc-ink)] grid place-items-center mb-6">
          <Lock className="h-5 w-5 text-[var(--lc-cream)]" />
        </div>
        <div className="eyebrow text-[var(--lc-moss)] mb-3">Admin Access</div>
        <h1 className="font-serif-display text-3xl leading-tight">
          Sign in to <span className="italic">manage leads.</span>
        </h1>
        <p className="text-sm text-[var(--lc-muted)] mt-3">
          Enter your admin PIN to view bookings and quote requests.
        </p>

        <div className="mt-7">
          <label className="eyebrow text-[var(--lc-muted)] block mb-2">
            Admin PIN
          </label>
          <input
            type="password"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="••••••••"
            className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-base focus:outline-none focus:border-[var(--lc-ink)] tracking-[0.3em]"
          />
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !pin}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Unlock
        </button>
      </form>
    </section>
  );
}

function StatCard({ label, value, hint, icon: Icon }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow text-[var(--lc-muted)]">{label}</span>
        <Icon className="h-4 w-4 text-[var(--lc-moss)]" />
      </div>
      <div className="font-serif-display text-3xl lg:text-4xl leading-none">{value}</div>
      {hint && <div className="mt-2 text-xs text-[var(--lc-muted)]">{hint}</div>}
    </div>
  );
}

function BookingsTable({ items, pin, onUpdate }) {
  const [busy, setBusy] = useState(null);
  const change = async (id, status) => {
    setBusy(id);
    try {
      await updateBookingStatus(pin, id, status);
      onUpdate();
    } finally {
      setBusy(null);
    }
  };
  if (items.length === 0)
    return (
      <div className="bg-white border border-[var(--lc-line)] rounded-md p-12 text-center">
        <Inbox className="h-8 w-8 mx-auto text-[var(--lc-muted)] mb-3" />
        <div className="font-serif-display text-xl">No bookings yet</div>
        <p className="text-sm text-[var(--lc-muted)] mt-1">
          Submissions from the Book page appear here.
        </p>
      </div>
    );
  return (
    <div className="space-y-3">
      {items.map((b) => (
        <article
          key={b.id}
          className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6"
        >
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-serif-display text-2xl leading-tight">{b.name}</h3>
                <StatusPill
                  value={b.status}
                  options={BOOKING_STATUSES}
                  onChange={(s) => change(b.id, s)}
                  loading={busy === b.id}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--lc-muted)]">
                <a
                  href={`mailto:${b.email}`}
                  className="inline-flex items-center gap-1.5 hover:text-[var(--lc-ink)]"
                >
                  <Mail className="h-3.5 w-3.5" /> {b.email}
                </a>
                {b.phone && (
                  <a
                    href={`tel:${b.phone}`}
                    className="inline-flex items-center gap-1.5 hover:text-[var(--lc-ink)]"
                  >
                    <Phone className="h-3.5 w-3.5" /> {b.phone}
                  </a>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {b.preferred}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> {formatDate(b.created_at)}
                </span>
              </div>
              {b.interest && (
                <div className="mt-3 text-xs">
                  <span className="eyebrow text-[var(--lc-muted)]">Interest:</span>{" "}
                  <span className="text-[var(--lc-ink)] font-semibold capitalize">{b.interest}</span>
                </div>
              )}
              {b.goals && (
                <p className="mt-3 text-sm text-[var(--lc-ink)] bg-[var(--lc-cream-soft)] p-3 rounded-sm border border-[var(--lc-line)]">
                  {b.goals}
                </p>
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function QuotesTable({ items, pin, onUpdate }) {
  const [busy, setBusy] = useState(null);
  const change = async (id, status) => {
    setBusy(id);
    try {
      await updateQuoteStatus(pin, id, status);
      onUpdate();
    } finally {
      setBusy(null);
    }
  };
  if (items.length === 0)
    return (
      <div className="bg-white border border-[var(--lc-line)] rounded-md p-12 text-center">
        <FileText className="h-8 w-8 mx-auto text-[var(--lc-muted)] mb-3" />
        <div className="font-serif-display text-xl">No quote requests yet</div>
        <p className="text-sm text-[var(--lc-muted)] mt-1">
          Custom package submissions appear here.
        </p>
      </div>
    );
  return (
    <div className="space-y-3">
      {items.map((q) => (
        <article
          key={q.id}
          className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6"
        >
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-serif-display text-2xl leading-tight">{q.name}</h3>
                <StatusPill
                  value={q.status}
                  options={QUOTE_STATUSES}
                  onChange={(s) => change(q.id, s)}
                  loading={busy === q.id}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--lc-muted)]">
                <a
                  href={`mailto:${q.email}`}
                  className="inline-flex items-center gap-1.5 hover:text-[var(--lc-ink)]"
                >
                  <Mail className="h-3.5 w-3.5" /> {q.email}
                </a>
                {q.phone && (
                  <a
                    href={`tel:${q.phone}`}
                    className="inline-flex items-center gap-1.5 hover:text-[var(--lc-ink)]"
                  >
                    <Phone className="h-3.5 w-3.5" /> {q.phone}
                  </a>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> {formatDate(q.created_at)}
                </span>
              </div>
              {q.notes && (
                <p className="mt-3 text-sm text-[var(--lc-ink)] bg-[var(--lc-cream-soft)] p-3 rounded-sm border border-[var(--lc-line)]">
                  {q.notes}
                </p>
              )}
              <details className="mt-4 group">
                <summary className="cursor-pointer text-xs font-semibold tracking-[0.18em] uppercase text-[var(--lc-moss)] hover:text-[var(--lc-moss-dark)] list-none flex items-center gap-1.5">
                  <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                  {q.items.length} services selected
                </summary>
                <ul className="mt-3 divide-y divide-[var(--lc-line)] border-t border-[var(--lc-line)]">
                  {q.items.map((it, idx) => (
                    <li key={idx} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                      <div>
                        <div className="font-semibold">{it.name}</div>
                        <div className="text-xs text-[var(--lc-muted)]">
                          {it.pillar_name} · CHF {it.price} / {it.unit} × {it.qty}
                        </div>
                      </div>
                      <div className="text-sm font-semibold">CHF {(it.price * it.qty).toFixed(0)}</div>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
            <div className="lg:text-right lg:min-w-[160px]">
              <div className="eyebrow text-[var(--lc-muted)]">Total</div>
              <div className="font-serif-display text-3xl mt-1">CHF {q.total.toFixed(0)}</div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function AssessmentsTable({ items, pin, onUpdate }) {
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const change = async (id, status) => {
    setBusy(id);
    try {
      await updateAssessmentStatus(pin, id, status);
      onUpdate();
    } finally {
      setBusy(null);
    }
  };
  if (items.length === 0)
    return (
      <div className="bg-white border border-[var(--lc-line)] rounded-md p-12 text-center">
        <FileText className="h-8 w-8 mx-auto text-[var(--lc-muted)] mb-3" />
        <div className="font-serif-display text-xl">No assessments yet</div>
        <p className="text-sm text-[var(--lc-muted)] mt-1">
          Submitted longevity questionnaires appear here.
        </p>
      </div>
    );
  return (
    <div className="space-y-3">
      {items.map((a) => {
        const isOpen = expanded === a.id;
        const circleTag = a.circle === "blue" ? "Blue Circle" : "Pink Circle";
        const circleColor = a.circle === "blue" ? "#3E5A7A" : "#B08585";
        const priorityId = a.data?.goals?.top_priority;
        const goals = a.data?.goals?.top_goals || [];
        return (
          <article
            key={a.id}
            className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif-display text-2xl leading-tight">{a.name}</h3>
                  <span
                    className="text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 rounded-full border"
                    style={{ color: circleColor, borderColor: circleColor + "50" }}
                  >
                    {circleTag}
                  </span>
                  <StatusPill
                    value={a.status}
                    options={ASSESSMENT_STATUSES}
                    onChange={(s) => change(a.id, s)}
                    loading={busy === a.id}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--lc-muted)]">
                  <a
                    href={`mailto:${a.email}`}
                    className="inline-flex items-center gap-1.5 hover:text-[var(--lc-ink)]"
                  >
                    <Mail className="h-3.5 w-3.5" /> {a.email}
                  </a>
                  {a.phone && (
                    <a
                      href={`tel:${a.phone}`}
                      className="inline-flex items-center gap-1.5 hover:text-[var(--lc-ink)]"
                    >
                      <Phone className="h-3.5 w-3.5" /> {a.phone}
                    </a>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" /> {formatDate(a.created_at)}
                  </span>
                </div>
                {(goals.length > 0 || priorityId) && (
                  <div className="mt-3 text-xs">
                    <span className="eyebrow text-[var(--lc-muted)]">Priority:</span>{" "}
                    <span className="text-[var(--lc-ink)] font-semibold">{priorityId || "—"}</span>
                    <span className="text-[var(--lc-muted)]"> · {goals.length} goals selected</span>
                  </div>
                )}
                <button
                  onClick={() => setExpanded(isOpen ? null : a.id)}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[var(--lc-moss)] hover:text-[var(--lc-moss-dark)]"
                >
                  <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  {isOpen ? "Hide details" : "View full questionnaire"}
                </button>
                {isOpen && (
                  <div className="mt-4 space-y-3 bg-[var(--lc-cream-soft)] border border-[var(--lc-line)] rounded-sm p-4 text-xs">
                    {Object.entries(a.data || {}).map(([section, obj]) => (
                      <details key={section} open className="group">
                        <summary className="cursor-pointer eyebrow text-[var(--lc-moss)] py-1">
                          {section}
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap break-words text-[11px] leading-relaxed text-[var(--lc-ink)] font-sans-ui">
                          {JSON.stringify(obj, null, 2)}
                        </pre>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function AdminPage() {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("clients");
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = useCallback(
    async (currentPin) => {
      const p = currentPin || pin;
      if (!p) return;
      setRefreshing(true);
      try {
        const [b, q, a, s] = await Promise.all([
          listBookings(p),
          listQuotes(p),
          listAssessments(p),
          fetchStats(p),
        ]);
        setBookings(b);
        setQuotes(q);
        setAssessments(a);
        setStats(s);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem(PIN_KEY);
          setAuthed(false);
          setPin("");
        }
      } finally {
        setRefreshing(false);
      }
    },
    [pin]
  );

  // Verify stored pin on mount
  useEffect(() => {
    const stored = localStorage.getItem(PIN_KEY);
    if (!stored) {
      setChecking(false);
      return;
    }
    verifyAdmin(stored)
      .then(() => {
        setAuthed(true);
        return loadAll(stored);
      })
      .catch(() => {
        localStorage.removeItem(PIN_KEY);
        setPin("");
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (p) => {
    setPin(p);
    setAuthed(true);
    await loadAll(p);
  };

  const onLogout = () => {
    localStorage.removeItem(PIN_KEY);
    setPin("");
    setAuthed(false);
    setBookings([]);
    setQuotes([]);
    setAssessments([]);
    setStats(null);
  };

  if (checking) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-[var(--lc-cream)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-muted)]" />
      </section>
    );
  }

  if (!authed) {
    return <LoginScreen onLogin={onLogin} />;
  }

  return (
    <section className="bg-[var(--lc-cream)] min-h-screen pt-[100px] lg:pt-[120px] pb-24">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow text-[var(--lc-moss)] mb-3">Admin Dashboard</div>
            <h1 className="font-serif-display text-[clamp(34px,5vw,64px)] leading-[1] font-light">
              Leads & <span className="italic">requests.</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => downloadMobileApiSpec(pin)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[var(--lc-line)] text-[12px] font-bold tracking-[0.18em] uppercase hover:border-[var(--lc-ink)] transition-colors"
              data-testid="admin-download-mobile-spec"
            >
              <FileDown className="h-3.5 w-3.5" />
              Mobile API spec
            </button>
            <button
              onClick={() => loadAll()}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[var(--lc-line)] text-[12px] font-bold tracking-[0.18em] uppercase hover:border-[var(--lc-ink)] transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard
              label="Assessments"
              value={stats.assessments?.total || 0}
              hint={`${stats.assessments?.new || 0} new`}
              icon={FileText}
            />
            <StatCard
              label="Bookings"
              value={stats.bookings.total}
              hint={`${stats.bookings.new} new`}
              icon={Inbox}
            />
            <StatCard
              label="Quote Requests"
              value={stats.quotes.total}
              hint={`${stats.quotes.new} new`}
              icon={FileText}
            />
            <StatCard
              label="Quotes Won"
              value={stats.quotes.won}
              hint={`CHF ${Math.round(stats.quotes.won_value || 0).toLocaleString()} value`}
              icon={TrendingUp}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[var(--lc-line)] overflow-x-auto no-scrollbar">
          {[
            { id: "clients", label: "Clients" },
            { id: "sessions", label: "Sessions" },
            { id: "doctors", label: "Doctors" },
            { id: "assessments", label: `Assessments (${assessments.length})` },
            { id: "bookings", label: `Bookings (${bookings.length})` },
            { id: "quotes", label: `Quote Requests (${quotes.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-[12px] font-bold tracking-[0.18em] uppercase border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.id
                  ? "border-[var(--lc-ink)] text-[var(--lc-ink)]"
                  : "border-transparent text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "clients" && <AdminClientsPanel pin={pin} />}
        {tab === "sessions" && <AdminSessionsPanel pin={pin} />}
        {tab === "doctors" && <AdminDoctorsPanel pin={pin} />}
        {tab === "bookings" && (
          <BookingsTable items={bookings} pin={pin} onUpdate={() => loadAll()} />
        )}
        {tab === "quotes" && (
          <QuotesTable items={quotes} pin={pin} onUpdate={() => loadAll()} />
        )}
        {tab === "assessments" && (
          <AssessmentsTable items={assessments} pin={pin} onUpdate={() => loadAll()} />
        )}
      </div>
    </section>
  );
}
